import { bindDialog, BindDialogReturn, DialogProps, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { IconButton, MenuItem, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, type JSX } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { getObjectHistoryItemTypeName, IObjectHistoryMemoItem } from "../../queries/object-history/Common";
import { useSaveHistoryItem } from "../../queries/object-history/UseSaveHistoryItem";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import FormDateTimePicker from "../forms/form-date-time-picker/FormDateTimePicker";
import FormInput from "../forms/form-input/FormInput";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { useMemoAutoText } from "../../queries/memo-auto-text/UseMemoAutoText";
import { bindCameraDialog, CameraDialog, useCameraDialogState } from "../camera-dialog/CameraDialog";
import FileViewerDialog, { bindFileViewerDialog, useFileViewerDialogState } from "../file-viewer-dialog/FileViewerDialog";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
import FormAttachmentCard, { FormAttachmentActionCardEnum } from "../forms/form-attachment-card/FormAttachmentCard";
import { IAttachment } from "../../common/models/IAttachment";
import { getAttachmentUrl } from "../../common/utilities/AttachmentHelpers";
import { FilestoreTypeEnum } from "../../common/enums/FilestoreTypeEnum";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import { AddAPhoto } from "@mui/icons-material";
import FormProperty from "../forms/form-property/FormProperty";

export type MemoEditDialogInitValues = IObjectHistoryMemoItem;

export type MemoEditDialogProps = {
    groupID: number;
    contextObject: IeLynxObject;
} & DialogProps<MemoEditDialogInitValues>;

type MemoDialogFormValuesType = {
    text: string;
    timestamp?: Date;
    autoTextIndex: number;

    attachments: IAttachment[];
};

export default function MemoEditDialog({
    open,
    onClose,
    values,
    groupID,
    contextObject
}: MemoEditDialogProps): JSX.Element {

    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();
    const isAddMode = (values?.memoID ?? -1) <= -1;
    const { confirm, acknowledge } = useConfirmation();

    const cameraDialog = useCameraDialogState();
    const fileViewerDialog = useFileViewerDialogState();
    // const fileSelectDialog = useFileSelectDialogState;
    const fileDownloaderRef = useRef<FileDownloaderType>(null);

    const { data: memoAutoText/*, status*/ } = useQueryNotifications(useMemoAutoText)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        objectType: contextObject.objectType ?? eLynxObjectTypeEnum.None,
        offline: offlineState.offline
    });

    const { mutate: saveHistoryItem/*, status: saveStatus*/ } = useQueryNotifications(useSaveHistoryItem)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        contextObject: contextObject,
        offline: offlineState.offline,
        onSuccess: (_data, variables) => {
            const itemName = getObjectHistoryItemTypeName(variables);
            acknowledge(t("saveSuccessMessage", { itemName: itemName }));
        }
    });

    const form = useForm<MemoDialogFormValuesType>({
        mode: "onChange",
        defaultValues: {
            text: "",
            timestamp: new Date(),
            autoTextIndex: -1
        } as MemoDialogFormValuesType
    });

    const {
        control,
        handleSubmit,
        formState: {
            isDirty,
            isValid,
        },
        reset,
        getValues,
        setValue,
        trigger,
    } = form;


    const {
        fields: attachments,
        append: appendAttachments,
        remove: removeAttachment
    } = useFieldArray({
        control: control,
        name: "attachments",
        keyName: "fieldKey"
    });


    useEffect(() => {
        if (open && values) {
            reset({
                text: values?.text ?? "",
                timestamp: values?.timestamp ?? new Date(),
                autoTextIndex: -1,
                attachments: values?.attachments
            });
        }
    }, [reset, open, values]);

    async function onOkay(data: MemoDialogFormValuesType) {
        const memoItem = {
            ...(values ?? {}),
            text: data.text,
            timestamp: data.timestamp,
            attachments: data.attachments
        } as IObjectHistoryMemoItem;

        saveHistoryItem(memoItem);

        reset();

        onClose && onClose({
            reason: "ok"
        });
    }

    async function onCancel() {

        //console.log('memo dialog oncancel');
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: isAddMode
                ? t("memoDialog.cancelAddText")
                : t("memoDialog.cancelEditText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function onAutoTextSelect() {
        const autoTextIndex = getValues("autoTextIndex");

        if(autoTextIndex > -1){
            const autoTextItem = memoAutoText?.find(a=>a.id === autoTextIndex);
    
            const text = getValues("text");
            const concat = `${text} ${autoTextItem?.autoText}`;
    
            setValue("text", concat.trim());
            // force validation
            trigger('text');
        }
    }


    async function onAttachmentAction(action: FormAttachmentActionCardEnum, index: number, attachment: IAttachment) {
        if (action === FormAttachmentActionCardEnum.View) {

            await fileViewerDialog.open({
                title: attachment.description,
                url: getUrl(attachment.base64Data, attachment.url),
                mimeType: attachment.mimeType
            });
        } else if (action === FormAttachmentActionCardEnum.Delete) {

            removeAttachment(index);
        } else if (action === FormAttachmentActionCardEnum.Download) {

            fileDownloaderRef.current?.download(getAttachmentUrl(attachment.base64Data, attachment.url), attachment.fileName);
        }
    }

    function getUrl(base64Data: string | undefined, url: string | undefined): string | undefined {
        return base64Data
            ? `data:image/jpeg;base64,${base64Data}`
            : url;
    }

    async function onOpenCamera() {
        const result = await cameraDialog.open();

        if (result.reason === "ok") {
            const regex = /-([0-9]*?)\./;
            const maxNumber = attachments
                .filter(i => i.fileName?.startsWith("Image"))
                .reduce<number>((prev, cur) => {

                    const fileName = cur.fileName ?? "";
                    const matched = regex.exec(fileName);

                    if (matched && matched.length > 0) {
                        const num: number = parseInt(matched[1], 10);
                        return num > prev ? num : prev;
                    }

                    return prev;
                }, 0);

            const fileName = `Image-${maxNumber < 9 ? 0 : ""}${maxNumber + 1}`;

            const attachment: IAttachment = {
                id: -1,
                fileName: `${fileName}.jpg`,
                description: fileName,
                deleted: false,
                filestoreType: FilestoreTypeEnum.Images,
                lastModified: undefined,
                mimeType: ApplicationMimeTypesEnum.JPEG,
                base64Data: result.values?.base64imageData,
                url: undefined,
                thumbnailUrl: undefined
            } as IAttachment;

            appendAttachments(attachment);
        }
    }
    
    return (
        <>
            <ResponsiveDialog
                title={isAddMode ? t("memoDialog.addMemo") : t("memoDialog.editMemo")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>
                    <FormInput
                        name="text"
                        label="Memo"
                        multiline={true}
                        rows={6}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: true
                        }}
                    />

                    <FormSelect
                        name="autoTextIndex"
                        label="Insert Text"
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: false
                        }}
                        onChange={onAutoTextSelect}
                    >
                        <MenuItem key={-1} value={-1} disabled={true}>(Select auto text)</MenuItem>
                        {memoAutoText?.map(o =>
                            <MenuItem key={o.id} value={o.id}>{o.autoText}</MenuItem>
                        )}
                    </FormSelect>

                    <FormDateTimePicker
                        name="timestamp"
                        label="Timestamp"
                        sx={{
                            marginTop: 2,
                            marginBottom: 2
                        }}
                        rules={{
                            required: true
                        }}
                    />


                    <FormPropertySection label={
                        <Stack spacing={0} direction="row" sx={{
                            alignItems: "center"
                        }}>
                            <Typography component="span" variant="subtitle1" sx={{
                                fontWeight: 600
                            }} >
                                Attachments
                            </Typography>
                            <IconButton color="inherit" onClick={
                                () => onOpenCamera()
                            }>
                                <AddAPhoto />
                            </IconButton>
                        </Stack>
                    }>

                        {attachments.map((field, index) => (
                            <FormProperty key={field.fieldKey}>
                                <FormAttachmentCard
                                    name={`attachments.${index}`}
                                    index={index}
                                    onAction={onAttachmentAction} />
                            </FormProperty>
                        ))}

                    </FormPropertySection>

                </Form>

            </ResponsiveDialog>
            <CameraDialog {...bindCameraDialog(cameraDialog)} />
            <FileViewerDialog {...bindFileViewerDialog(fileViewerDialog)} />
            <FileDownloader ref={fileDownloaderRef} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type MemoEditDialogState = DialogState<MemoEditDialogInitValues>;
export type BindMemoEditDialogReturn = BindDialogReturn<MemoEditDialogInitValues>;

export function useMemoEditDialogState(): MemoEditDialogState {
    return useDialogState();
}

export function bindMemoEditDialog(props: MemoEditDialogState): BindMemoEditDialogReturn {
    return bindDialog(props);
}




